import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { User, UserRole, UserStatus } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button, Card, Input } from '../../components'
import {
  AdminApiError,
  listAdminUsers,
  setAdminUserDisabled,
  setAdminUserRole,
} from '../../lib/admin-api.js'
import { showToast } from '../../components/Toast'
import styles from './UsersPage.module.css'

type RoleFilterValue = UserRole | 'all'
type StatusFilterValue = UserStatus | 'all'

const ROLE_OPTIONS: Array<{ value: RoleFilterValue; label: string }> = [
  { value: 'all', label: 'All roles' },
  { value: 'pending', label: 'Pending' },
  { value: 'student', label: 'Student' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'admin', label: 'Admin' },
]

const STATUS_OPTIONS: Array<{ value: StatusFilterValue; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
]

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

export default function UsersPage(): JSX.Element {
  const { firebaseUser, uid, refresh } = useAuth()
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<RoleFilterValue>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all')
  const [users, setUsers] = useState<User[]>([])
  const [nextPageToken, setNextPageToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null)
  const [pendingRoles, setPendingRoles] = useState<Record<string, UserRole>>({})
  const [busyActionKey, setBusyActionKey] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const roleSelectRefs = useRef<Record<string, HTMLSelectElement | null>>({})

  const getIdToken = useCallback(async () => {
    if (!firebaseUser) throw new Error('Admin session missing')
    return firebaseUser.getIdToken()
  }, [firebaseUser])

  const syncPendingRoles = useCallback((nextUsers: User[]) => {
    setPendingRoles((previous) => {
      const draft = { ...previous }
      for (const user of nextUsers) {
        draft[user.id] = draft[user.id] ?? user.role
      }
      return draft
    })
  }, [])

  const loadUsers = useCallback(
    async (append: boolean, pageToken?: string | null) => {
      if (!firebaseUser) return
      const setter = append ? setLoadingMore : setLoading
      setter(true)
      if (!append) setErrorMessage(null)

      try {
        const response = await listAdminUsers(getIdToken, {
          search,
          role: roleFilter,
          status: statusFilter,
          pageSize: 25,
          pageToken: pageToken ?? null,
        })
        setUsers((previous) => {
          const nextUsers = append ? [...previous, ...response.users] : response.users
          syncPendingRoles(nextUsers)
          return nextUsers
        })
        setNextPageToken(response.nextPageToken)
      } catch (err) {
        const message =
          err instanceof AdminApiError || err instanceof Error
            ? err.message
            : 'Unable to load users right now.'
        setErrorMessage(message)
        showToast(message, 'error')
      } finally {
        setter(false)
      }
    },
    [firebaseUser, getIdToken, roleFilter, search, statusFilter, syncPendingRoles],
  )

  useEffect(() => {
    void loadUsers(false, null)
  }, [loadUsers])

  useEffect(() => {
    if (!expandedUserId) return
    roleSelectRefs.current[expandedUserId]?.focus()
  }, [expandedUserId])

  const summary = useMemo(() => {
    if (loading) return 'Loading user accounts.'
    if (users.length === 0) return 'No users match your filters.'
    return `${users.length} user${users.length === 1 ? '' : 's'} in this view.`
  }, [loading, users.length])

  async function handleRoleSubmit(user: User) {
    const nextRole = pendingRoles[user.id] ?? user.role
    if (nextRole === user.role) {
      setExpandedUserId(null)
      return
    }

    setBusyActionKey(`role:${user.id}`)
    try {
      const updatedUser = await setAdminUserRole(getIdToken, user.id, { role: nextRole })
      setUsers((previous) => previous.map((entry) => (entry.id === user.id ? updatedUser : entry)))
      setPendingRoles((previous) => ({ ...previous, [user.id]: updatedUser.role }))
      setExpandedUserId(null)
      const message = `${updatedUser.displayName} is now ${updatedUser.role}.`
      setAnnouncement(message)
      showToast(message, 'success')
      if (uid === updatedUser.id) {
        await refresh()
      }
    } catch (err) {
      const message =
        err instanceof AdminApiError || err instanceof Error
          ? err.message
          : 'Unable to update that role right now.'
      setAnnouncement(message)
      showToast(message, 'error')
    } finally {
      setBusyActionKey(null)
    }
  }

  async function handleDisableToggle(user: User) {
    const disabled = user.status !== 'disabled'
    setBusyActionKey(`disabled:${user.id}`)
    try {
      const updatedUser = await setAdminUserDisabled(getIdToken, user.id, { disabled })
      setUsers((previous) => previous.map((entry) => (entry.id === user.id ? updatedUser : entry)))
      setExpandedUserId(null)
      const message = disabled
        ? `${updatedUser.displayName} has been disabled.`
        : `${updatedUser.displayName} has been re-enabled.`
      setAnnouncement(message)
      showToast(message, 'success')
    } catch (err) {
      const message =
        err instanceof AdminApiError || err instanceof Error
          ? err.message
          : 'Unable to change that account status right now.'
      setAnnouncement(message)
      showToast(message, 'error')
    } finally {
      setBusyActionKey(null)
    }
  }

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Phase 2</p>
          <h2>Users</h2>
        </div>
        <p className={styles.headerCopy}>
          Promote teachers, pause accounts, and keep role changes visible and reversible through
          audit history.
        </p>
      </div>

      <Card className={styles.filters} elevated>
        <div className={styles.filterGrid}>
          <Input
            label="Search users"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name or email"
          />
          <div className={styles.selectField}>
            <label htmlFor="admin-role-filter" className={styles.selectLabel}>
              Role
            </label>
            <select
              id="admin-role-filter"
              className={styles.select}
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value as RoleFilterValue)}
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.selectField}>
            <label htmlFor="admin-status-filter" className={styles.selectLabel}>
              Status
            </label>
            <select
              id="admin-status-filter"
              className={styles.select}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilterValue)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <p role="status" aria-live="polite" className="sr-only">
        {announcement || summary}
      </p>

      <Card className={styles.tableCard} elevated>
        <div className={styles.tableHeader}>
          <div>
            <h3>User directory</h3>
            <p className={styles.muted}>{summary}</p>
          </div>
          {!loading && users.length > 0 && (
            <p className={styles.muted}>
              Actions open inline for keyboard and screen-reader users.
            </p>
          )}
        </div>

        {errorMessage && !loading ? (
          <div className={styles.stateBlock}>
            <p role="alert">{errorMessage}</p>
            <Button type="button" variant="secondary" onClick={() => void loadUsers(false, null)}>
              Retry
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div className={styles.stateBlock}>
            <p>Loading users…</p>
          </div>
        ) : null}

        {!loading && users.length === 0 && !errorMessage ? (
          <div className={styles.stateBlock}>
            <p>No users match your filters.</p>
          </div>
        ) : null}

        {!loading && users.length > 0 ? (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className="sr-only">Admin user management table</caption>
                <thead>
                  <tr>
                    <th scope="col">User</th>
                    <th scope="col">Domain</th>
                    <th scope="col">Role</th>
                    <th scope="col">Status</th>
                    <th scope="col">Joined</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const panelId = `admin-user-actions-${user.id}`
                    const roleValue = pendingRoles[user.id] ?? user.role
                    const isRoleBusy = busyActionKey === `role:${user.id}`
                    const isStatusBusy = busyActionKey === `disabled:${user.id}`

                    return (
                      <Fragment key={user.id}>
                        <tr key={user.id}>
                          <th scope="row" className={styles.userCell}>
                            <span className={styles.displayName}>{user.displayName}</span>
                            <span className={styles.email}>{user.email}</span>
                          </th>
                          <td>{user.domain || 'Unknown'}</td>
                          <td>
                            <span
                              className={[
                                styles.badge,
                                styles[`role${capitalize(user.role)}`],
                              ].join(' ')}
                              aria-label={`Role: ${user.role}`}
                              title={`Role: ${user.role}`}
                            >
                              {user.role}
                            </span>
                          </td>
                          <td>
                            <span
                              className={[
                                styles.badge,
                                styles[`status${capitalize(user.status)}`],
                              ].join(' ')}
                              aria-label={`Status: ${user.status}`}
                            >
                              {user.status}
                            </span>
                          </td>
                          <td>{formatDate(user.createdAt)}</td>
                          <td className={styles.actionsCell}>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              aria-expanded={expandedUserId === user.id}
                              aria-controls={panelId}
                              onClick={() =>
                                setExpandedUserId((current) =>
                                  current === user.id ? null : user.id,
                                )
                              }
                            >
                              {expandedUserId === user.id ? 'Close actions' : 'Open actions'}
                            </Button>
                          </td>
                        </tr>
                        {expandedUserId === user.id ? (
                          <tr>
                            <td colSpan={6} className={styles.panelCell}>
                              <div id={panelId} className={styles.actionPanel}>
                                <div className={styles.actionPanelSection}>
                                  <label
                                    htmlFor={`role-select-${user.id}`}
                                    className={styles.selectLabel}
                                  >
                                    Change role
                                  </label>
                                  <div className={styles.inlineControls}>
                                    <select
                                      id={`role-select-${user.id}`}
                                      ref={(node) => {
                                        roleSelectRefs.current[user.id] = node
                                      }}
                                      className={styles.select}
                                      value={roleValue}
                                      onChange={(event) =>
                                        setPendingRoles((previous) => ({
                                          ...previous,
                                          [user.id]: event.target.value as UserRole,
                                        }))
                                      }
                                    >
                                      {ROLE_OPTIONS.filter((option) => option.value !== 'all').map(
                                        (option) => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ),
                                      )}
                                    </select>
                                    <Button
                                      type="button"
                                      size="sm"
                                      loading={isRoleBusy}
                                      disabled={roleValue === user.role}
                                      onClick={() => void handleRoleSubmit(user)}
                                    >
                                      Confirm role
                                    </Button>
                                  </div>
                                </div>

                                <div className={styles.actionPanelSection}>
                                  <p className={styles.actionHeading}>Account access</p>
                                  <div className={styles.inlineControls}>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant={user.status === 'disabled' ? 'secondary' : 'danger'}
                                      loading={isStatusBusy}
                                      onClick={() => void handleDisableToggle(user)}
                                    >
                                      {user.status === 'disabled'
                                        ? 'Enable account'
                                        : 'Disable account'}
                                    </Button>
                                    <span className={styles.actionHint}>
                                      {user.status === 'disabled'
                                        ? 'The user can sign back in after re-enabling.'
                                        : 'Disabling revokes future refresh tokens for this account.'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {nextPageToken ? (
              <div className={styles.loadMore}>
                <Button
                  type="button"
                  variant="secondary"
                  loading={loadingMore}
                  onClick={() => void loadUsers(true, nextPageToken)}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </Card>
    </section>
  )
}

function capitalize(value: string) {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`
}

function formatDate(value: string) {
  if (!value) return 'Unknown'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return DATE_FORMATTER.format(parsed)
}
